// ChatDialog — Floating chat dialog for direct messaging
// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { directChatAPI } from "@/lib/api";
import useChatStore from "@/stores/chatStore";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  X,
  Send,
  Loader2,
  MessageCircle,
  Minus,
} from "lucide-react";

const getInitials = (f, l) =>
  `${(f || "")[0] || ""}${(l || "")[0] || ""}`.toUpperCase();

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Hier";
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
};

export default function ChatDialog({ emitTyping }) {
  const { user } = useAuth();
  const {
    chatOpen,
    chatTarget,
    activeConversationId,
    closeChat,
    openChat,
    setActiveConversation,
    decrementUnread,
  } = useChatStore();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [convId, setConvId] = useState(null); // local conversation ID
  const [conversations, setConversations] = useState([]); // for conversation list view
  const [loadingConvs, setLoadingConvs] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  // Reset state when chat closes or target changes
  useEffect(() => {
    if (!chatOpen) {
      setMessages([]);
      setConvId(null);
      setNewMessage("");
      setLoading(false);
      setMinimized(false);
      setConversations([]);
    }
  }, [chatOpen]);

  // Load conversation list when opened without a target
  useEffect(() => {
    if (!chatOpen || chatTarget || !user) return;
    let cancelled = false;
    const loadConversations = async () => {
      setLoadingConvs(true);
      try {
        const res = await directChatAPI.listConversations();
        if (!cancelled) setConversations(res.data || []);
      } catch (err) {
        console.error("Failed to load conversations:", err);
      } finally {
        if (!cancelled) setLoadingConvs(false);
      }
    };
    loadConversations();
    return () => { cancelled = true; };
  }, [chatOpen, chatTarget, user?.id]);

  // Create or get conversation when dialog opens
  useEffect(() => {
    if (!chatOpen || !chatTarget || !user) return;

    let cancelled = false;

    const initConversation = async () => {
      setLoading(true);
      setMessages([]);
      try {
        const canCreate = ["admin", "trainer", "manager"].includes(user.role);
        let conversationId = activeConversationId;

        if (canCreate && !conversationId) {
          const res = await directChatAPI.createConversation(chatTarget.id);
          if (!cancelled) {
            conversationId = res.data.id;
            setActiveConversation(conversationId);
          }
        } else if (!canCreate && !conversationId) {
          // Candidate: find existing conversation
          const convRes = await directChatAPI.listConversations();
          if (!cancelled) {
            const existing = convRes.data.find(c =>
              c.participant_ids.includes(chatTarget.id)
            );
            if (existing) {
              conversationId = existing.id;
              setActiveConversation(conversationId);
            }
          }
        }

        if (!cancelled && conversationId) {
          setConvId(conversationId);
          // Load messages
          const msgRes = await directChatAPI.listMessages(conversationId);
          if (!cancelled) {
            setMessages(msgRes.data || []);
            scrollToBottom();
            // Mark as read
            try {
              await directChatAPI.markAsRead(conversationId);
              decrementUnread(conversationId);
            } catch (_) {}
          }
        }
      } catch (err) {
        console.error("Failed to init conversation:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initConversation();
    return () => { cancelled = true; };
  }, [chatOpen, chatTarget?.id, user?.id]);

  // Also update local convId when store changes (e.g. from notification click)
  useEffect(() => {
    if (activeConversationId && activeConversationId !== convId) {
      setConvId(activeConversationId);
      // Load messages for this conversation
      directChatAPI.listMessages(activeConversationId).then(res => {
        setMessages(res.data || []);
        scrollToBottom();
        directChatAPI.markAsRead(activeConversationId).catch(() => {});
      }).catch(console.error);
    }
  }, [activeConversationId]);

  // Listen for incoming messages (from socket)
  useEffect(() => {
    const handler = (e) => {
      const { message } = e.detail;
      if (message && message.conversation_id === convId) {
        setMessages((prev) => [...prev, message]);
        scrollToBottom();
        directChatAPI.markAsRead(convId).catch(() => {});
      }
    };

    const typingHandler = (e) => {
      const { conversation_id, is_typing: typing } = e.detail;
      if (conversation_id === convId) {
        setIsTyping(typing);
        if (typing) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
        }
      }
    };

    window.addEventListener("chat:incoming_message", handler);
    window.addEventListener("chat:typing_indicator", typingHandler);
    return () => {
      window.removeEventListener("chat:incoming_message", handler);
      window.removeEventListener("chat:typing_indicator", typingHandler);
    };
  }, [convId, scrollToBottom]);

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || !convId || sending) return;

    const content = newMessage.trim();
    setNewMessage("");
    setSending(true);

    // Optimistic update
    const optimisticMsg = {
      id: "temp-" + Date.now(),
      conversation_id: convId,
      sender_id: user.id,
      sender_name: `${user.first_name} ${user.last_name}`,
      sender_avatar: user.avatar,
      content,
      created_at: new Date().toISOString(),
      read_by: [user.id],
      _sending: true,
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    scrollToBottom();

    try {
      const res = await directChatAPI.sendMessage(convId, content);
      setMessages((prev) =>
        prev.map((m) => (m.id === optimisticMsg.id ? res.data : m))
      );
    } catch (err) {
      console.error("Failed to send:", err);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (chatTarget && convId && emitTyping) {
      emitTyping(chatTarget.id, convId, true);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (emitTyping) emitTyping(chatTarget.id, convId, false);
      }, 2000);
    }
  };

  if (!chatOpen) return null;

  const roleColors = {
    admin: "#8b5cf6",
    trainer: "#3b82f6",
    candidate: "#22c55e",
    manager: "#f59e0b",
  };
  // If no chatTarget, show conversation list view
  if (!chatTarget) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col shadow-2xl border rounded-2xl overflow-hidden"
        style={{
          width: "380px",
          height: "480px",
          background: "var(--background)",
          borderColor: "var(--border)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: "linear-gradient(135deg, #8b5cf615, #8b5cf608)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div className="flex items-center gap-3">
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #8b5cf6, #6d28d9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MessageCircle style={{ width: 16, height: 16, color: "white" }} />
            </div>
            <div>
              <p className="text-sm font-semibold">Messages</p>
              <p className="text-[10px] text-muted-foreground">Vos conversations</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-lg hover:bg-red-500/10 hover:text-red-500"
            onClick={closeChat}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Conversation list */}
        <ScrollArea className="flex-1 px-2 py-2">
          {loadingConvs ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <MessageCircle className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">Aucune conversation</p>
              <p className="text-xs mt-1 opacity-60">Vos messages apparaîtront ici</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {conversations.map(conv => {
                const other = conv.participants?.find(p => p.id !== user?.id) || {};
                const rc = roleColors[other.role] || "#8b5cf6";
                const unread = conv.unread_count || 0;
                return (
                  <div
                    key={conv.id}
                    onClick={() => {
                      const target = {
                        id: other.id,
                        first_name: other.first_name || other.name?.split(" ")[0] || "",
                        last_name: other.last_name || other.name?.split(" ").slice(1).join(" ") || "",
                        role: other.role,
                        avatar: other.avatar,
                      };
                      console.log("[DirectChat] Opening conversation with:", target, "conv:", conv.id);
                      setConversations([]);
                      openChat(target, conv.id);
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 12px", borderRadius: 12, cursor: "pointer",
                      background: unread > 0 ? `${rc}08` : "transparent",
                      border: unread > 0 ? `1px solid ${rc}20` : "1px solid transparent",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--muted)"}
                    onMouseLeave={e => e.currentTarget.style.background = unread > 0 ? `${rc}08` : "transparent"}
                  >
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={other.avatar} />
                        <AvatarFallback className="text-xs font-bold text-white" style={{ background: rc }}>
                          {getInitials(other.first_name || other.name, other.last_name || "")}
                        </AvatarFallback>
                      </Avatar>
                      {unread > 0 && (
                        <span style={{
                          position: "absolute", top: -4, right: -4,
                          minWidth: 18, height: 18, borderRadius: 9,
                          background: "#ef4444", color: "white",
                          fontSize: 10, fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          padding: "0 4px", border: "2px solid var(--background)",
                        }}>
                          {unread}
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <p style={{ fontSize: 13, fontWeight: unread > 0 ? 700 : 500, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {other.first_name || other.name || "?"} {other.last_name || ""}
                        </p>
                        {conv.last_message_at && (
                          <span style={{ fontSize: 10, color: "var(--muted-foreground)", flexShrink: 0, marginLeft: 8 }}>
                            {formatTime(conv.last_message_at)}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 11, color: "var(--muted-foreground)", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: unread > 0 ? 600 : 400 }}>
                        {(typeof conv.last_message === "object" && conv.last_message !== null) ? (conv.last_message.content || "Aucun message") : (conv.last_message || "Aucun message")}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
    );
  }

  const targetColor = roleColors[chatTarget.role] || "#8b5cf6";

  // Group messages by date
  let lastDate = null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col shadow-2xl border rounded-2xl overflow-hidden"
      style={{
        width: minimized ? "280px" : "380px",
        height: minimized ? "56px" : "520px",
        background: "var(--background)",
        borderColor: "var(--border)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        style={{
          background: `linear-gradient(135deg, ${targetColor}15, ${targetColor}08)`,
          borderBottom: "1px solid var(--border)",
        }}
        onClick={() => minimized && setMinimized(false)}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-8 w-8 ring-2" style={{ "--tw-ring-color": targetColor + "40" }}>
              <AvatarImage src={chatTarget.avatar} />
              <AvatarFallback
                className="text-[10px] font-bold text-white"
                style={{ background: targetColor }}
              >
                {getInitials(chatTarget.first_name, chatTarget.last_name)}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-background" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">
              {chatTarget.first_name} {chatTarget.last_name}
            </p>
            {isTyping ? (
              <p className="text-[10px] font-medium animate-pulse" style={{ color: targetColor }}>
                est en train d'écrire...
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground capitalize">
                {chatTarget.role === "trainer" ? "Formateur" : chatTarget.role === "candidate" ? "Candidat" : chatTarget.role}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-lg"
            onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-lg hover:bg-red-500/10 hover:text-red-500"
            onClick={(e) => { e.stopPropagation(); closeChat(); }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Messages area */}
      {!minimized && (
        <>
          <ScrollArea className="flex-1 px-4 py-3">
            {loading ? (
              <div className="flex items-center justify-center h-full py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-muted-foreground">
                <MessageCircle className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm font-medium">Aucun message</p>
                <p className="text-xs mt-1 opacity-60">
                  Envoyez votre premier message !
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {messages.map((msg, idx) => {
                  const isMine = msg.sender_id === user?.id;
                  const msgDate = formatDate(msg.created_at);
                  let showDate = false;
                  if (msgDate !== lastDate) {
                    lastDate = msgDate;
                    showDate = true;
                  }

                  return (
                    <div key={msg.id || idx}>
                      {showDate && (
                        <div className="flex justify-center my-3">
                          <span className="text-[10px] text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">
                            {msgDate}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                            isMine
                              ? "rounded-br-md"
                              : "rounded-bl-md"
                          }`}
                          style={{
                            background: isMine
                              ? `linear-gradient(135deg, ${targetColor}, ${targetColor}cc)`
                              : "var(--muted)",
                            color: isMine ? "#fff" : "var(--foreground)",
                            opacity: msg._sending ? 0.6 : 1,
                          }}
                        >
                          <p className="text-[13px] leading-relaxed break-words whitespace-pre-wrap">
                            {msg.content}
                          </p>
                          <p
                            className="text-[9px] mt-1 text-right"
                            style={{ opacity: 0.6 }}
                          >
                            {formatTime(msg.created_at)}
                            {msg._sending && " ⏳"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input area */}
          <div className="px-3 py-3 border-t" style={{ borderColor: "var(--border)" }}>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
            >
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={handleInputChange}
                placeholder="Écrire un message..."
                className="flex-1 rounded-xl text-sm h-9"
                disabled={!convId || loading}
                autoFocus
              />
              <Button
                type="submit"
                size="icon"
                className="h-9 w-9 rounded-xl shrink-0"
                disabled={!newMessage.trim() || !convId || sending}
                style={{
                  background: newMessage.trim()
                    ? `linear-gradient(135deg, ${targetColor}, ${targetColor}cc)`
                    : undefined,
                }}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
